import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Modal, Form, Input, ModalHeader, ModalBody, ModalFooter, Row, Col, Card, CardBody, Container, FormGroup, Label, FormFeedback, Spinner } from 'reactstrap';
import { FaPlus, FaEdit, FaTrash, FaRegBuilding, FaCubes, FaTruckMoving, FaTruckPickup, FaTruckLoading } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { categoryActions } from '../../sagas/categorySlice';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { transporteurActions } from '../../sagas/transporteurSlice';

const initForm = {
    code: '',
    libelle: '',
    telephone1: '',
    telephone2: '',
    cin: '',
    matricule: '',
}
const TransporteurPage = () => {
    const dispatch = useDispatch();
    const { transporteurs, loading } = useSelector((state) => state.transporteurs); // Adjust state path as needed
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingTransporteur, setEditingTransporteur] = useState(null);
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(transporteurActions.list()); // Fetch categories on component mount
    }, [dispatch]);

    useEffect(() => {

    }, [transporteurs]);

    const formik = useFormik({
        initialValues: editingTransporteur ? editingTransporteur : initForm,
        enableReinitialize: true,
        validationSchema: Yup.object({
            libelle: Yup.string().required('Libelle is required'),
        }),
        onSubmit: (values) => {
            if (editingTransporteur) {
                let payload = {
                    id: editingTransporteur.id,
                    data: {
                        code: values.code,
                        libelle: values.libelle,
                        telephone1: values.telephone1,
                        telephone2: values.telephone2,
                        cin: values.cin,
                        matricule: values.matricule,
                    },
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: 'Enregistrement réussi',
                            text: 'Le transporteur a été ajouté avec succès.',
                        })

                        handleCloseModal();
                    },
                    onError: () => {
                        Swal.fire({
                            icon: 'error',
                            title: 'Erreur',
                            text: 'Une erreur s\'est produite lors de l\'ajout du transporteur.',
                        });
                    }
                }

                dispatch(transporteurActions.updateTransporteur(payload));
                setEditingTransporteur(null);
            } else {
                let payload = {
                    data: {
                        code: values.code,
                        libelle: values.libelle,
                        telephone1: values.telephone1,
                        telephone2: values.telephone2,
                        cin: values.cin,
                        matricule: values.matricule,
                    },
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: 'Enregistrement réussi',
                            text: 'Le transporteur a été ajouté avec succès.',
                        })

                        handleCloseModal();
                    },
                    onError: () => {
                        Swal.fire({
                            icon: 'error',
                            title: 'Erreur',
                            text: 'Une erreur s\'est produite lors de l\'ajout du transporteur.',
                        });
                    }
                }

                dispatch(transporteurActions.addTransporteur(payload));
            }

        }
    });

    const { values, getFieldProps, errors, handleSubmit, setFieldValue } = formik;



    const handleCloseModal = () => {
        setIsModalVisible(false);
        formik.resetForm();
        dispatch(transporteurActions.list()); // Fetch categories on component mount
    };

    const toggleModal = () => {
        setIsModalVisible(!isModalVisible);
        setEditingTransporteur(null);
    };


    const handleEditTransporteur = (transporteur) => {
        setEditingTransporteur(transporteur);
        setIsModalVisible(true);
    };

    const handleDeleteTransporteur = (id) => {
        Swal.fire({
            title: t('common.areYouSure'),
            text: t('transporteur.deleteConfirmation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('common.delete'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(transporteurActions.delete({
                    id,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.deleted'),
                            text: t('transporteur.deleteSuccess'),
                        });
                        dispatch(transporteurActions.list());
                    },
                    onError: () => {
                        Swal.fire({
                            icon: 'error',
                            title: t('common.error'),
                            text: t('transporteur.deleteError'),
                        });
                    }
                }));
            }
        });
    };

    return (
        <Container fluid>
            {loading && (
                <div className="d-flex justify-content-center align-items-center vh-100">
                    <Spinner color="primary" size="lg" />
                </div>
            )
            }
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaTruckPickup className="text-primary me-2" />
                                {t('transporteur.title')}
                            </h4>
                            <Button
                                color="primary"
                                onClick={() => toggleModal()} // Go back to the previous page
                                className="d-flex align-items-center"
                            >
                                <FaPlus className="me-2" />
                                {t('transporteur.new')}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <Table bordered>
                            <thead>
                                <tr>
                                    <th>{t('transporteur.code', 'Code')}</th>
                                    <th>{t('transporteur.label', 'Nom')}</th>
                                    <th>{t('transporteur.telephone', 'Téléphone')}</th>
                                    <th>{t('transporteur.cin', 'CIN')}</th>
                                    <th>{t('transporteur.matricule', 'Matricule')}</th>
                                    <th>{t('common.actions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transporteurs && transporteurs.length > 0 ? transporteurs.map((transporteur) => (
                                    <tr key={transporteur.id}>
                                        <td>{transporteur.code}</td>
                                        <td>{transporteur.libelle}</td>
                                        <td>{transporteur.telephone1}</td>
                                        <td>{transporteur.cin}</td>
                                        <td>{transporteur.matricule}</td>
                                        <td>
                                            <Button
                                                color="warning"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handleEditTransporteur(transporteur)}
                                            >
                                                <FaEdit />
                                            </Button>
                                            <Button
                                                color="danger"
                                                size="sm"
                                                onClick={() => handleDeleteTransporteur(transporteur.id)}
                                                disabled
                                            >
                                                <FaTrash />
                                            </Button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            <div className="text-center mt-4">
                                                <FaTruckPickup size={50} className="text-muted" />
                                                <h5 className="mt-2">{t('transporteur.noTransporteurs', 'Aucun transporteur trouvé')}</h5>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    
                </CardBody>
            </Card>
            <Modal isOpen={isModalVisible} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {editingTransporteur ? t('transporteur.edit', 'Modifier le transporteur') : t('transporteur.new', 'Nouveau transporteur')}
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={formik.handleSubmit}>
                        <FormGroup>
                            <Label for="code">{t('transporteur.code', 'Code')}</Label>
                            <Input
                                type="text"
                                name="code"
                                id="code"
                                value={formik.values.code || ''}
                                onChange={formik.handleChange}
                                disabled
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="libelle">{t('transporteur.label', 'Nom')} *</Label>
                            <Input
                                type="text"
                                name="libelle"
                                id="libelle"
                                value={formik.values.libelle || ''}
                                onChange={formik.handleChange}
                                invalid={formik.touched.libelle && Boolean(formik.errors.libelle)}
                            />
                            <FormFeedback>{formik.errors.libelle}</FormFeedback>
                        </FormGroup>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="telephone1">{t('transporteur.telephone', 'Téléphone')} *</Label>
                                    <Input
                                        type="text"
                                        name="telephone1"
                                        id="telephone1"
                                        value={formik.values.telephone1 || ''}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.telephone1 && Boolean(formik.errors.telephone1)}
                                    />
                                    <FormFeedback>{formik.errors.telephone1}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="telephone2">{t('transporteur.telephone2', 'Téléphone 2')}</Label>
                                    <Input
                                        type="text"
                                        name="telephone2"
                                        id="telephone2"
                                        value={formik.values.telephone2 || ''}
                                        onChange={formik.handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="cin">{t('transporteur.cin', 'CIN')}</Label>
                                    <Input
                                        type="text"
                                        name="cin"
                                        id="cin"
                                        value={formik.values.cin || ''}
                                        onChange={formik.handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="matricule">{t('transporteur.matricule', 'Matricule')}</Label>
                                    <Input
                                        type="text"
                                        name="matricule"
                                        id="matricule"
                                        value={formik.values.matricule || ''}
                                        onChange={formik.handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={formik.handleSubmit}>
                        {editingTransporteur ? t('common.update', 'Mettre à jour') : t('common.save', 'Enregistrer')}
                    </Button>{' '}
                    <Button color="secondary" onClick={toggleModal}>
                        {t('common.cancel', 'Annuler')}
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default TransporteurPage;